import {
  AtendimentoObservacaoViewQuery,
  AtendimentoProfissional,
  AtendimentoProfissionalPreNatal,
  LotacaoOuEstagio,
  Medicao,
  PreNatal,
  StatusAtendimento,
  TipoAtendimentoProfissional,
  TipoEstabelecimentoEnum,
} from 'graphql/types.generated'
import { isCboEnfermeiro, isCboMedico } from 'util/atendimento/cboHelper'
import { isUndefinedOrNull } from 'util/checks'
import { EvolucaoProblema } from 'view/atendimentos/detail/soap/aside/types/ProblemaModel'
import { ProblemaCondicaoModel } from 'view/atendimentos/detail/soap/avaliacao/components/problemas-condicoes/model-problemasCondicoes'
import { isAutomatico } from 'view/atendimentos/detail/soap/avaliacao/components/problemas-condicoes/utils/verifications-problemasCondicoes'
import { convertAvaliacaoQueryToForm } from 'view/atendimentos/detail/soap/avaliacao/converter'
import { convertFinalizacaoQueryToForm } from 'view/atendimentos/detail/soap/finalizacao'
import { convertObjetivoQueryToForm } from 'view/atendimentos/detail/soap/objetivo'
import { convertPlanoQueryToForm } from 'view/atendimentos/detail/soap/plano/converter-plano'
import { PreNatalViewModel } from 'view/atendimentos/detail/soap/pre-natal/model-preNatal'
import { convertSubjetivoQueryToForm } from 'view/atendimentos/detail/soap/subjetivo'

import { SoapEditableItem, SoapState } from '../model'
import { isSoapEditableItem, SoapEditableModules } from '../util-atendimentoIndividual'
import {
  AtendimentoObservacaoModel,
  AtendimentoObservacaoResponsavelModel,
  AtendimentoProfissionalObservacaoInput,
  OBSERVACAO_DISABLE_REMOVE_TOOLTIP_TEXT,
} from './model-atendObservacao'

interface AtendimentoProfissionalPreNatalModel extends Omit<AtendimentoProfissionalPreNatal, 'preNatal'> {
  preNatal?: Omit<PreNatal, 'problema' | 'ultimaDum'>
}

type AtendimentoProfissionalObservacaoModel = AtendimentoObservacaoViewQuery['atendimento']['atendimentoProfissional']

type MedicaoModel = Omit<Medicao, 'atendimentoProfissional' | 'dataMedicao'>

const LISTA_ATENDIMENTO_OBSERVACAO_STATUS = [StatusAtendimento.AGUARDANDO_OBSERVACAO, StatusAtendimento.EM_OBSERVACAO]

export const getResponsavelPelaObservacao = (
  lotacaoId: ID,
  atendObsResponsaveis: ReadonlyArray<AtendimentoObservacaoResponsavelModel>,
  estagioId: ID,
  isEmProcessoRevisao: boolean
): AtendimentoObservacaoResponsavelModel => {
  if (isUndefinedOrNull(lotacaoId) || isUndefinedOrNull(atendObsResponsaveis)) {
    return null
  }

  return atendObsResponsaveis.find(
    (atendObsResponsavel) =>
      (isEmProcessoRevisao && estagioId ? estagioId : lotacaoId) === atendObsResponsavel.responsavel.id
  )
}

export const getAtendimentoObservacaoModel = (
  atendimentoProfissional: AtendimentoProfissionalObservacaoInput,
  isEmProcessoRevisao: boolean,
  isAcessoEstagio: boolean
): AtendimentoObservacaoModel => {
  const isAtendimentoObservacao = !isUndefinedOrNull(atendimentoProfissional?.atendimentoProfissionalObservacao)

  const lotacaoId = atendimentoProfissional?.lotacao?.id
  const estagioId = atendimentoProfissional?.estagio?.id
  const atendObsResponsaveis = isAtendimentoObservacao
    ? atendimentoProfissional.atendimentoProfissionalObservacao.atendimentoObservacao.responsaveis
    : null
  const responsavelObservacao = getResponsavelPelaObservacao(
    lotacaoId,
    atendObsResponsaveis,
    estagioId,
    isEmProcessoRevisao
  )

  const isObservacaoAndResponsavel =
    isAtendimentoObservacao && !isAcessoEstagio && !isUndefinedOrNull(responsavelObservacao)
  const isObservacaoAndAuxiliar = isAtendimentoObservacao && !isObservacaoAndResponsavel
  const ultimoResponsavelObservacao =
    atendimentoProfissional?.atendimentoProfissionalObservacao?.atendimentoObservacao?.ultimoResponsavelObservacao
  const responsaveisObservacao = atendObsResponsaveis?.map((value) => value.responsavel)
  const atendeuComoResponsavel = isObservacaoAndResponsavel ? responsavelObservacao.atendeuComoResponsavel : null
  const isObservacaoEmAndamento = isAtendimentoObservacao && !isUndefinedOrNull(atendimentoProfissional?.finalizadoEm)

  return {
    isAtendimentoObservacao,
    isObservacaoAndResponsavel,
    isObservacaoAndAuxiliar,
    isObservacaoEmAndamento,
    ultimoResponsavelObservacao,
    responsaveisObservacao,
    atendeuComoResponsavel,
  }
}

export const isAtendimentoObservacaoByStatusAtendimento = (statusAtendimento: StatusAtendimento): boolean => {
  return LISTA_ATENDIMENTO_OBSERVACAO_STATUS.includes(statusAtendimento)
}

export const getEditableItemRemoveTooltipText = <T extends SoapEditableItem>(
  item: T,
  defaultTooltipText: string = 'Excluir',
  editableItemTooltipText: string = OBSERVACAO_DISABLE_REMOVE_TOOLTIP_TEXT
): { isEditable: boolean; tooltipText: string } => {
  const isEditable = isSoapEditableItem(item)
  const tooltipText = isEditable ? editableItemTooltipText : defaultTooltipText

  return {
    isEditable,
    tooltipText,
  }
}

export const extractObservacaoEditableModules = (
  atendimentoProfissional: AtendimentoProfissionalObservacaoModel,
  shouldClearEncaminhamentosCache: boolean
): SoapEditableModules => {
  return {
    evolucaoSubjetivo: atendimentoProfissional?.evolucaoSubjetivo,
    evolucaoObjetivo: atendimentoProfissional?.evolucaoObjetivo,
    evolucaoAvaliacao: atendimentoProfissional?.evolucaoAvaliacao,
    evolucaoPlano: atendimentoProfissional?.evolucaoPlano,
    orientacoes: atendimentoProfissional?.orientacoes,
    atestados: atendimentoProfissional?.atestados,
    encaminhamentos: shouldClearEncaminhamentosCache ? [] : atendimentoProfissional?.encaminhamentos,
    encaminhamentosEspecializados: shouldClearEncaminhamentosCache
      ? []
      : atendimentoProfissional?.encaminhamentosEspecializados,
    guiasEncaminhamento: atendimentoProfissional?.guiasEncaminhamento,
    marcadorConsumoAlimentar: atendimentoProfissional?.marcadorConsumoAlimentar,
    resultadosExames: atendimentoProfissional?.examesResultados,
    solicitacoesExames: [
      ...(atendimentoProfissional?.requisicoesExames || []),
      ...(atendimentoProfissional?.solicitacoesOci || []),
    ],
    receitasMedicamento: atendimentoProfissional?.receitaMedicamento,
    evolucoesProblema: atendimentoProfissional?.evolucaoProblema,
    finalizacao: {
      tipoAtendimento: atendimentoProfissional?.tipoAtendimento,
      condutas: atendimentoProfissional?.condutas,
      racionalidadeSaude: atendimentoProfissional?.racionalidadeSaude,
      lotacaoAtendimentoCompartilhado: atendimentoProfissional?.lotacaoAtendimentoCompartilhado,
      notificacoesCasoSuspeito: atendimentoProfissional?.notificacoesCasoSuspeito,
      procedimentosAtendimento: atendimentoProfissional?.procedimentosAtendimento,
      tipoParticipacaoCidadao: atendimentoProfissional?.tipoParticipacaoCidadao,
      tipoParticipacaoProfissionalConvidado: atendimentoProfissional?.tipoParticipacaoProfissionalConvidado,
    },
  }
}

export const extractPreNatalFromAtendimentoProfissional = (atendimentoProfissional: {
  medicoes?: ReadonlyArray<MedicaoModel>
  atendimentoProfissionalPreNatal?: AtendimentoProfissionalPreNatalModel
}): PreNatalViewModel => {
  const medicaoDesteAtendimento = atendimentoProfissional?.medicoes?.find((medicao) => !medicao.medicaoAnterior)

  return {
    id: atendimentoProfissional?.atendimentoProfissionalPreNatal?.preNatal.id,
    alturaUterina: medicaoDesteAtendimento?.valorAlturaUterina,
    batimentoCardiacoFetal: medicaoDesteAtendimento?.valorBatimentoCardiacoFetal,
    batimentoCardiacoFetal2: medicaoDesteAtendimento?.valorBatimentoCardiacoFetal2,
    edema: atendimentoProfissional?.atendimentoProfissionalPreNatal?.tipoEdema,
    gravidezPlanejada: atendimentoProfissional?.atendimentoProfissionalPreNatal?.gravidezPlanejada,
    movimentacaoFetal: atendimentoProfissional?.atendimentoProfissionalPreNatal?.movimentacaoFetal,
    tipoGravidez: atendimentoProfissional?.atendimentoProfissionalPreNatal?.preNatal?.tipoGravidez?.tipoGravidez,
    isAltoRisco: atendimentoProfissional?.atendimentoProfissionalPreNatal?.preNatal?.altoRisco,
  }
}

export const initialValueObservacao = (
  prevValue: Partial<SoapState>,
  dataReferencia: Instant,
  tipoAtendimento: TipoAtendimentoProfissional,
  hasIvcfPreenchidoEmAtendObservacaoAnterior: boolean,
  data: SoapEditableModules,
  emailCidadao: string,
  atendimentoProfissional: AtendimentoProfissional
): Partial<SoapState> => {
  const {
    evolucaoSubjetivo,
    evolucaoObjetivo,
    evolucaoAvaliacao,
    evolucaoPlano,
    finalizacao,
    evolucoesProblema,
    resultadosExames,
    solicitacoesExames,
    orientacoes,
    receitasMedicamento,
    encaminhamentos,
    encaminhamentosEspecializados,
    guiasEncaminhamento,
    atestados,
    marcadorConsumoAlimentar,
  } = data

  return {
    ...prevValue,
    subjetivo: convertSubjetivoQueryToForm(prevValue.subjetivo, evolucaoSubjetivo),
    objetivo: convertObjetivoQueryToForm(
      prevValue.objetivo,
      tipoAtendimento,
      hasIvcfPreenchidoEmAtendObservacaoAnterior,
      {
        evolucaoObjetivo,
        resultadosExames,
        marcadorConsumoAlimentar,
      }
    ),
    avaliacao: convertAvaliacaoQueryToForm(prevValue.avaliacao, {
      evolucaoAvaliacao,
      evolucoesProblema,
    }),
    plano: convertPlanoQueryToForm(
      prevValue.plano,
      {
        evolucaoPlano,
        solicitacoesExames,
        orientacoes,
        receitasMedicamento,
        encaminhamentos,
        encaminhamentosEspecializados,
        guiasEncaminhamento,
        atestados,
        dataReferencia,
      },
      emailCidadao,
      atendimentoProfissional
    ),
    finalizacao: convertFinalizacaoQueryToForm(prevValue.finalizacao, finalizacao),
  }
}

export const podeFinalizarAtendimentoObservacao = (
  acesso: LotacaoOuEstagio,
  tipoEstabelecimento: TipoEstabelecimentoEnum
): boolean => {
  const isMedico = isCboMedico(acesso.cbo.cbo2002)
  const isEnfermeiro = isCboEnfermeiro(acesso.cbo.cbo2002)

  const isUpa = tipoEstabelecimento === TipoEstabelecimentoEnum.UPA
  const cboPodeLiberarEmUpa = isUpa && isMedico
  const cboPodeLiberar = !isUpa && (isMedico || isEnfermeiro)

  return cboPodeLiberarEmUpa || cboPodeLiberar
}

export const podeDeletarProblemaCondicaoEmAtendObservacao = (
  problema: ProblemaCondicaoModel,
  evolucoesAnterioresDestaObservacao: ReadonlyArray<EvolucaoProblema>,
  tipoAtendimentoProfissional: TipoAtendimentoProfissional
): boolean => {
  const isPreNatal = tipoAtendimentoProfissional === TipoAtendimentoProfissional.PRE_NATAL
  const { isEditable } = getEditableItemRemoveTooltipText(problema)
  const isProblemaPreNatalAnteriorAdicionadoAutomaticamente =
    isPreNatal &&
    isAutomatico(problema) &&
    evolucoesAnterioresDestaObservacao.some(
      ({ problema: problemaAnterior }) =>
        problemaAnterior.ciap?.codigo === problema.ciap?.codigo &&
        problemaAnterior.cid10?.codigo === problema.cid?.codigo
    )

  return !isEditable && !isProblemaPreNatalAnteriorAdicionadoAutomaticamente
}

export const isAtendimentoObservacaoComPuericultura = (
  isAtendimentoObservacao: boolean,
  tipoAtendimentoProfissional: TipoAtendimentoProfissional
): boolean => isAtendimentoObservacao && tipoAtendimentoProfissional === TipoAtendimentoProfissional.PUERICULTURA
